/**
 * Recent searches — the per-user "previous searches" panel.
 *
 * A "🕘 Recent" toggle that opens a dropdown of the user's prior searches
 * (combined across main + keyword + letter, newest first), each shown with a
 * mode badge, the query as chips (AND/OR operators for keyword), a filter
 * summary, result count, and relative time. Clicking a row replays the EXACT
 * search; ✕ removes one; "Clear all" wipes the list.
 *
 * Backed by useSearchHistory (server, per-user). Used on both the main and
 * keyword search tabs.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { describeSearch, relativeTime } from '../utils/describeSearch';
import type { SearchHistoryEntry } from '../types';

const MODE_BADGE: Record<string, string> = {
  keyword: 'bg-accent-tint text-accent-deep ring-accent-ring',
  main: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  letter: 'bg-amber-50 text-amber-700 ring-amber-200',
};

export default function RecentSearches({
  onReplay,
  align = 'left',
}: {
  onReplay: (entry: SearchHistoryEntry) => void;
  align?: 'left' | 'right';
}) {
  const { history, isLoading, removeSearch, clearHistory } = useSearchHistory();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const count = history.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-secondary h-[34px] px-3 text-[13px] font-normal"
        title="Your recent searches"
      >
        Recent searches
        {count > 0 && (
          <span className="font-mono text-[12px] text-ink-3">{count}</span>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-30 mt-1 w-[28rem] max-w-[90vw] overflow-hidden rounded-lg border border-line bg-white shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex items-center justify-between border-b border-line-subtle bg-ground px-3 py-2">
            <span className="text-xs font-medium uppercase tracking-wider text-ink-3">Recent searches</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => clearHistory()}
                className="text-xs text-ink-3 transition-colors hover:text-red-600"
              >
                Clear all
              </button>
            )}
          </div>

          {isLoading && count === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-ink-3">Loading…</div>
          ) : count === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-ink-3">No previous searches yet.</div>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {history.map((entry) => {
                const d = describeSearch(entry);
                const replayable = !!(entry.payload && (entry.payload as Record<string, unknown>)['url']);
                return (
                  <li key={entry.id} className="group flex items-start hover:bg-ground">
                    <button
                      type="button"
                      disabled={!replayable}
                      onClick={() => {
                        onReplay(entry);
                        setOpen(false);
                      }}
                      className="min-w-0 flex-1 px-3 py-2.5 text-left disabled:cursor-not-allowed"
                      title={replayable ? 'Re-run this search' : 'This entry cannot be replayed'}
                    >
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ring-1 ${
                            MODE_BADGE[d.mode] ?? MODE_BADGE['main']
                          }`}
                        >
                          {d.modeLabel}
                        </span>
                        {d.terms.length > 0 ? (
                          d.terms.map((t, i) => (
                            <span key={i} className="inline-flex items-center gap-1">
                              {t.op && <span className="text-[10px] font-semibold text-ink-3">{t.op}</span>}
                              <span className="rounded bg-panel px-1.5 py-0.5 text-xs text-ink-2">{t.term}</span>
                            </span>
                          ))
                        ) : (
                          <span className="truncate text-sm font-medium text-ink">{d.text || '(empty)'}</span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-3">
                        {d.filters.map((f, i) => (
                          <span key={i}>{f}</span>
                        ))}
                        {entry.result_count != null && (
                          <span>· {entry.result_count.toLocaleString()} results</span>
                        )}
                        <span>· {relativeTime(entry.created_at)}</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSearch(entry.id)}
                      className="px-2 py-2.5 text-line-input opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"
                      title="Remove from history"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
